import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LRL_TOKENS } from '@lrl/ui-tokens';
// SVG layout constants
const CELL_W = 140;
const CELL_H = 52;
const CELL_GAP = 16;
const COL_GAP = 56;
const PADDING = 16;
// Stage column order
const STAGES = ['qf', 'sf', 'f', '3rd'];
const STAGE_LABELS = {
    qf: '1/4 финала',
    sf: '1/2 финала',
    f: 'Финал',
    '3rd': '3-е место',
};
const STAGE_SLOTS = {
    qf: 4,
    sf: 2,
    f: 1,
    '3rd': 1,
};
function colX(stageIndex) {
    return PADDING + stageIndex * (CELL_W + COL_GAP);
}
function cellY(slot, totalSlots, stageIndex) {
    // Vertically centre each column's cells within the height of the QF column
    const qfHeight = STAGE_SLOTS.qf * CELL_H + (STAGE_SLOTS.qf - 1) * CELL_GAP;
    const stageSlots = Math.max(1, totalSlots);
    const stageHeight = stageSlots * CELL_H + (stageSlots - 1) * CELL_GAP;
    const topOffset = PADDING + (qfHeight - stageHeight) / 2;
    // 3rd-place match sits below the QF column
    if (stageIndex === STAGES.indexOf('3rd')) {
        return PADDING + qfHeight + CELL_GAP * 2;
    }
    return topOffset + slot * (CELL_H + CELL_GAP);
}
function MatchCell({ x, y, match, label }) {
    const nameA = match?.player_a ?? 'TBD';
    const nameB = match?.player_b ?? 'TBD';
    const score = match?.score ?? null;
    const winnerId = match?.winner_id ?? null;
    const textA = score ? `${nameA}` : nameA;
    const textB = score ? `${nameB}` : nameB;
    return (_jsxs("g", { children: [_jsx("rect", { x: x, y: y, width: CELL_W, height: CELL_H, rx: Number(LRL_TOKENS.rXs.replace('px', '')), fill: LRL_TOKENS.card, stroke: LRL_TOKENS.rule, strokeWidth: 1 }), _jsx("line", { x1: x, y1: y + CELL_H / 2, x2: x + CELL_W, y2: y + CELL_H / 2, stroke: LRL_TOKENS.rule, strokeWidth: 0.5 }), _jsx("text", { x: x, y: y - 5, fontSize: 8, fontFamily: LRL_TOKENS.mono, fill: LRL_TOKENS.inkMuted, letterSpacing: "0.08em", textAnchor: "start", dominantBaseline: "alphabetic", style: { textTransform: 'uppercase' }, children: label }), _jsx("text", { x: x + 8, y: y + CELL_H / 4, fontSize: 10, fontFamily: LRL_TOKENS.sans, fill: winnerId !== null && winnerId === match?.player_a ? LRL_TOKENS.primary : LRL_TOKENS.ink, fontWeight: winnerId !== null && winnerId === match?.player_a ? '600' : '400', dominantBaseline: "middle", children: truncate(textA, 17) }), _jsx("text", { x: x + 8, y: y + (3 * CELL_H) / 4, fontSize: 10, fontFamily: LRL_TOKENS.sans, fill: winnerId !== null && winnerId === match?.player_b ? LRL_TOKENS.primary : LRL_TOKENS.ink, fontWeight: winnerId !== null && winnerId === match?.player_b ? '600' : '400', dominantBaseline: "middle", children: truncate(textB, 17) }), score && (_jsx("text", { x: x + CELL_W - 6, y: y + CELL_H / 2, fontSize: 9, fontFamily: LRL_TOKENS.mono, fill: LRL_TOKENS.inkSoft, textAnchor: "end", dominantBaseline: "middle", children: score }))] }));
}
function truncate(s, max) {
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
}
/**
 * SVG single-elimination bracket for up to 8-entry tournaments.
 * Columns: QF (4 matches), SF (2), F (1), 3rd-place (1).
 */
export function BracketTree({ matches }) {
    if (matches.length === 0)
        return null;
    // Group matches by stage
    const byStage = new Map();
    for (const m of matches) {
        const arr = byStage.get(m.stage) ?? [];
        arr.push(m);
        byStage.set(m.stage, arr);
    }
    // SVG dimensions
    const qfSlots = STAGE_SLOTS.qf;
    const qfHeight = qfSlots * CELL_H + (qfSlots - 1) * CELL_GAP;
    // 3rd-place adds extra row
    const svgH = PADDING * 2 + qfHeight + CELL_GAP * 2 + CELL_H;
    const svgW = PADDING * 2 + STAGES.length * CELL_W + (STAGES.length - 1) * COL_GAP;
    return (_jsx("div", { className: "overflow-x-auto", children: _jsx("svg", { width: svgW, height: svgH, viewBox: `0 0 ${svgW} ${svgH}`, style: { fontFamily: LRL_TOKENS.sans }, "aria-label": "Bracket", children: STAGES.map((stage, si) => {
                const stageMatches = byStage.get(stage) ?? [];
                const totalSlots = STAGE_SLOTS[stage];
                const x = colX(si);
                return Array.from({ length: totalSlots }).map((_, slot) => {
                    const match = stageMatches.find((m) => m.slot === slot);
                    const y = cellY(slot, totalSlots, si);
                    const showLabel = slot === 0;
                    return (_jsx(MatchCell, { x: x, y: y, match: match, label: showLabel ? STAGE_LABELS[stage] : '' }, `${stage}-${slot}`));
                });
            }) }) }));
}
